import type { PageConfig, FormConfig } from '@devfactory/core';
import { ComponentRenderer } from './ComponentRenderer';

// =============================================================================
// PAGE RENDERER
// =============================================================================

interface PageRendererProps {
  page: PageConfig;
  forms: FormConfig[];
}

export function PageRenderer({ page, forms }: PageRendererProps) {
  const layoutClasses: Record<string, string> = {
    default: '',
    centered: 'flex flex-col items-center justify-center min-h-screen',
    sidebar: 'flex',
    fullWidth: 'w-full',
    narrow: 'max-w-3xl mx-auto',
    wide: 'max-w-7xl mx-auto',
  };

  return (
    <div className={`${layoutClasses[page.layout] || ''} px-4 py-6 sm:px-6 lg:px-8`}>
      {page.components.map((component, index) => (
        <ComponentRenderer key={`${page.id}-${index}`} config={component} forms={forms} />
      ))}
    </div>
  );
}

// =============================================================================
// PAGE NOT FOUND
// =============================================================================

export function PageNotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-300 dark:text-gray-700">404</h1>
        <h2 className="mt-4 text-2xl font-semibold text-gray-900 dark:text-white">Page Not Found</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          Go Home
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// LOADING PAGE
// =============================================================================

export function LoadingPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}
